import { Bounce, ToastContainer, toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useGlobalContext } from '~/context/GlobalContext';
import { apiService } from '~/services/apiService';
import { IChatRoom } from '~/types/common';
import classNames from 'classnames';
import Header from '~/componentsPage/Header/Header';
import GenerelRoom from '~/componentsPage/HomePage/GeneralRoom';
import OnTopRoom from '~/componentsPage/HomePage/OnTopRoom';
import NewAcquaintanceRoom from '~/componentsPage/HomePage/NewAcquaintanceRoom';
import FantasiesRoom from '~/componentsPage/HomePage/FantasiesRoom';
import DifferentCountriesRoom from '~/componentsPage/HomePage/DifferentCountriesRoom';
import CloseArea from '~/componentsPage/HomePage/CloseArea';
import styles from '~/componentsPage/HomePage/styles/HomePageContainer.module.css';
import { CameraIcon, ChatIcon, UsersIcon } from '~/components/Icons/Icons';

const SECTION_TITLES: Record<string, string> = {
  general: 'Pokój Ogólny',
  onTop: 'Na Topie',
  newAcquaintance: 'Nowe Znajomości',
  fantasies: 'Fantazje',
  differentCountries: 'Świat',
  closeArea: 'Twoja Okolica',
};

const ScrollableRoomsGrid = ({ children }: { children: React.ReactNode }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const checkShadows = () => {
    const el = scrollRef.current;
    if (!el) return;
    const hasScroll = el.scrollHeight > el.clientHeight;
    const isAtTop = el.scrollTop <= 5;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 5;
    setShowTopShadow(hasScroll && !isAtTop);
    setShowBottomShadow(hasScroll && !isAtBottom);
  };

  useEffect(() => {
    checkShadows();
    window.addEventListener('resize', checkShadows);
    return () => window.removeEventListener('resize', checkShadows);
  }, [children]);

  return (
    <div className={styles.scrollWrapper}>
      <div className={`${styles.shadowIndicator} ${styles.topShadow} ${showTopShadow ? styles.visible : ''}`} />
      <div ref={scrollRef} className={styles.roomsGrid} onScroll={checkShadows}>
        {children}
      </div>
      <div className={`${styles.shadowIndicator} ${styles.bottomShadow} ${showBottomShadow ? styles.visible : ''}`} />
    </div>
  );
};

function HomePageContainer() {
  const navigate = useNavigate();
  const { context, setContext } = useGlobalContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [nicknameOption, setNicknameOption] = useState<'guest' | 'custom'>('guest');
  const [customNick, setCustomNick] = useState('');
  const [gender, setGender] = useState<'female' | 'male' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [roomSearchTerm, setRoomSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [showCookies, setShowCookies] = useState(false);

  useEffect(() => {
    getRooms();
    const consent = getCookie('cookies');
    const reason = window.sessionStorage.getItem('logout_reason');

    let cookieTimer: NodeJS.Timeout;

    if (reason === 'notActive') {
      notify("Zostałeś wylogowany z powodu braku aktywności.", "warning", 9000);
      window.sessionStorage.removeItem('logout_reason');
    } else if (reason === 'forceLogout') {
      notify("Zalogowano się na innym urządzeniu.", "warning", false);
      window.sessionStorage.removeItem('logout_reason');
    } else if (reason === 'banned' || reason === 'kicked') {
      notify("Zostałeś usunięty z czatu z powodu naruszenia regulaminu.", "error", false);
      window.sessionStorage.removeItem('logout_reason');
    }

    if (!consent) {
      cookieTimer = setTimeout(() => setShowCookies(true), 1000);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (cookieTimer) clearTimeout(cookieTimer);
    };
  }, []);

  const goToChat = async (roomId?: string) => {
    if (!gender) {
      notify("Wybierz płeć, aby wejść na czat.", "warning");
      return;
    }

    const finalNick = nicknameOption === 'guest' ? 'guest' : customNick.trim();
    if (nicknameOption === 'custom' && !finalNick) {
      notify("Wpisz swój nick lub wybierz opcję Gość.", "error");
      return;
    }

    if (finalNick.length > 30) {
      notify("Nick nie może być dłuższy niż 30 znaków.", "error");
      return;
    }

    const targetRoomId = roomId || context?.publicRooms?.[0]?.id || 'ogolny';

    const user = {
      name: finalNick,
      gender: gender,
      room: targetRoomId,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_SERVER}/api/chat/login` as string, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      });

      if (!response.ok) {
        throw new Error(`Błąd serwera: ${response.status}`);
      }

      const data = await response.json();

      if (data?.result?.isUserNameExist === false) {
        navigate('/chat', {
          state: {
            room: data.result.room,
            name: data.result.name,
            gender: data.result.gender,
          }
        });
        return;
      }

      notify("Ten nick jest już zajęty przez kogoś innego", "error");

    } catch (error) {
      console.error("API Error:", error);
      notify("Nie udało się połączyć z serwerem czatu", "error");
    }
  };

  const goToRoulette = async () => {
    if (!gender) {
      notify("Wybierz płeć, aby wejść na wideo ruletkę.", "warning");
      return;
    }

    const finalNick = nicknameOption === 'guest' ? 'guest' : customNick.trim();
    if (nicknameOption === 'custom' && !finalNick) {
      notify("Wpisz swój nick lub wybierz opcję Gość.", "error");
      return;
    }

    if (finalNick.length > 30) {
      notify("Nick nie może być dłuższy niż 30 znaków.", "error");
      return;
    }

    // Specjalny identyfikator pokoju dla ruletki
    const user = {
      name: finalNick,
      gender: gender,
      room: 'roulette',
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_SERVER}/api/chat/login` as string, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      });

      if (!response.ok) {
        throw new Error(`Błąd serwera: ${response.status}`);
      }

      const data = await response.json();

      if (data?.result?.isUserNameExist === false) {
        navigate('/roulette', {
          state: {
            name: data.result.name,
            gender: data.result.gender,
          }
        });
        return;
      }

      notify("Ten nick jest już zajęty przez kogoś innego", "error");

    } catch (error) {
      console.error("API Error:", error);
      notify("Nie udało się połączyć z serwerem czatu", "error");
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  };

  const getCookie = (name: string) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  const handleAcceptCookies = () => {
    setCookie('cookies', 'accepted', 90);
    setShowCookies(false);
  };

  const toggleSection = (label: string) => {
    setCollapsedSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const getRooms = async () => {
    setLoading(true);
    try {
      const data = await apiService('/chat/rooms', 'GET');
      setContext({ publicRooms: data });
    } catch (error) {
      console.error("Błąd pobierania pokoi:", error);
      notify("Nie udało się pobrać listy pokoi", "error");
    } finally {
      setLoading(false);
    }
  };

  const notify = (msg: string, type: 'error' | 'warning' | 'info', autoClose: number | false = 3000) => {
    const isDark = document.body.classList.contains('dark-mode');
    const BrandIcon = () => (
      <div style={{ fontWeight: 800, fontSize: '20px', color: 'var(--accent-color)', border: '2px solid var(--accent-color)', borderRadius: '5px', padding: '2px 4px', lineHeight: 1 }}>!</div>
    );
    toast[type](msg, {
      position: "top-center",
      autoClose: autoClose === false ? false : (autoClose || 3000),
      theme: isDark ? "dark" : "light",
      icon: <BrandIcon />,
      style: { borderRadius: '15px', border: '1px solid var(--border-color)', fontSize: '14px', fontWeight: '600' }
    });
  };

  const groupedRooms = useMemo(() => {
    const rooms = context?.publicRooms || [];
    const filtered = rooms.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return filtered.reduce((acc, room) => {
      const label = room.label || 'general';
      if (!acc[label]) acc[label] = [];
      acc[label].push(room);
      return acc;
    }, {} as Record<string, IChatRoom[]>);
  }, [context?.publicRooms, searchTerm]);

  const dropdownRoomsList = useMemo(() => {
    return (context?.publicRooms || []).filter(r =>
      r.name.toLowerCase().includes(roomSearchTerm.toLowerCase())
    );
  }, [context?.publicRooms, roomSearchTerm]);

  const renderRoomComponent = (item: IChatRoom) => {
    const props = { data: item, goToChat: () => goToChat(item.id) };
    switch (item.label) {
      case 'onTop': return <OnTopRoom key={item.id} {...props} />;
      case 'newAcquaintance': return <NewAcquaintanceRoom key={item.id} {...props} />;
      case 'fantasies': return <FantasiesRoom key={item.id} {...props} />;
      case 'differentCountries': return <DifferentCountriesRoom key={item.id} {...props} />;
      case 'closeArea': return <CloseArea key={item.id} {...props} />;
      default: return <GenerelRoom key={item.id} {...props} />;
    }
  };

  return (
    <div className={styles.homePageContainer}>
      <Header />
      <section className={styles.hero}>
        <div className={styles.loginBox}>
          <div className={styles.infoBubble}>
            <div className={styles.bubbleIcon}>💬</div>
            <div className={styles.bubbleText}>
              <strong>Czateria24.pl</strong> to miejsce fascynujących rozmów i nowych znajomości. Wejdź jako gość lub podaj własny nick – pisz na czacie albo wylosuj rozmówcę w wideo ruletce!
            </div>
          </div>

          <div className={styles.loginToggle}>
            <button className={`${styles.toggleBtn} ${nicknameOption === 'guest' ? styles.active : ''}`} onClick={() => setNicknameOption('guest')}>Gość</button>
            <button className={`${styles.toggleBtn} ${nicknameOption === 'custom' ? styles.active : ''}`} onClick={() => setNicknameOption('custom')}>Własny Nick</button>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.inputLabel}>Twój Nick:</label>
            <input
              type="text" className={classNames(styles.mainInput, {
                [styles.active]: nicknameOption !== 'guest'
              })}
              disabled={nicknameOption === 'guest'}
              maxLength={30}
              value={nicknameOption === 'guest' ? '' : customNick}
              onChange={(e) => setCustomNick(e.target.value)}
              placeholder={nicknameOption === 'guest' ? "Gość_..." : "Wpisz nick..."}
            />
            {nicknameOption === 'custom' && (
              <small style={{ color: customNick.length > 30 ? 'red' : 'inherit', fontSize: '10px' }}>
                {customNick.length}/30
              </small>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.inputLabel}>Twoja Płeć:</label>
            <div className={styles.genderGrid}>
              <button className={`${styles.genderBtn} ${gender === 'female' ? styles.selectedGender : ''}`} onClick={() => setGender('female')}>Kobieta</button>
              <button className={`${styles.genderBtn} ${gender === 'male' ? styles.selectedGender : ''}`} onClick={() => setGender('male')}>Mężczyzna</button>
            </div>
          </div>

          <div className={styles.actionSection}>
            <div className={styles.actionButtons}>
              <button className={`${styles.enterBtn} ${styles.rouletteBtn}`} onClick={goToRoulette}>
                WYLOSUJ ROZMÓWCĘ <CameraIcon className={styles.cameraIcon} />
              </button>
              <button className={styles.enterBtn} onClick={() => goToChat()}>
                ROZPOCZNIJ CZAT <ChatIcon className={styles.chatIcon} />
              </button>
            </div>
          </div>

          <div className={styles.separator}>lub wybierz pokój</div>

          <div className={styles.dropdownContainer} ref={dropdownRef}>
            <div className={`${styles.dropdownHeader} ${isDropdownOpen ? styles.dropdownActive : ''}`} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
              <span>{isDropdownOpen ? 'Wybierz z listy...' : 'Wszystkie pokoje'}</span>
              <span className={styles.arrow}>{isDropdownOpen ? '▲' : '▼'}</span>
            </div>
            {isDropdownOpen && (
              <div className={styles.dropdownContent}>
                <div className={styles.dropdownSearchWrapper}>
                  <input type="text" className={styles.dropdownSearchInput} placeholder="Szukaj pokoju..." autoFocus value={roomSearchTerm} onChange={(e) => setRoomSearchTerm(e.target.value)} />
                </div>
                <div className={styles.dropdownList}>
                  {dropdownRoomsList.map(room => (
                    <div key={room.id} className={styles.dropdownItem} onClick={() => { goToChat(room.id); setIsDropdownOpen(false); }}>
                      <span className={styles.roomName}>{room.name}</span>
                      <span className={styles.roomUserCount}><UsersIcon className={styles.usersIcon} />  {room.usersCount || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className={styles.container}>
        <div className={styles.searchSection}>
          <input type="text" placeholder="Szukaj pokoju na liście..." className={styles.searchInput} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>

        {loading ? (
          <div className={styles.loaderContainer}><div className={styles.loader}></div></div>
        ) : (
          Object.keys(groupedRooms).map((label) => {
            const rooms = (groupedRooms as any)[label];
            if (rooms.length === 0) return null;
            const isCollapsed = collapsedSections[label];

            return (
              <div key={label} className={styles.sectionGroup}>
                <div className={styles.sectionHeader} onClick={() => toggleSection(label)}>
                  <h2 className={styles.sectionTitle}>{SECTION_TITLES[label] || label}</h2>
                  <span className={`${styles.toggleArrow} ${isCollapsed ? styles.arrowRotated : ''}`}>▲</span>
                </div>
                <div className={`${styles.collapsibleContent} ${isCollapsed ? styles.collapsed : ''}`}>
                  <ScrollableRoomsGrid>{rooms.map(renderRoomComponent)}</ScrollableRoomsGrid>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showCookies && (
        <div className={styles.cookieBanner}>
          <div className={styles.cookieIcon}>🍪</div>
          <div className={styles.cookieText}>
            Używamy ciasteczek, aby serwis<strong>Czateria24</strong> działał bez zarzutu.
          </div>
          <button className={styles.cookieBtn} onClick={handleAcceptCookies}>Rozumiem</button>
        </div>
      )}

      <footer className={styles.mainFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerLinks}>
            <Link to="/pomoc">Pomoc</Link>
            <Link to="/kontakt">Kontakt</Link>
            <Link to="/polityka-prywatnosci">Polityka prywatności</Link>
            <Link to="/regulamin">Regulamin</Link>
          </div>
          <div className={styles.copyright}>© Czateria24. Wszystkie prawa zastrzeżone.</div>
        </div>
      </footer>
      <ToastContainer transition={Bounce} />
    </div>
  );
}

export default HomePageContainer;